import {
  Alert,
  Button,
  Grid,
  IconButton,
  InputLabel,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FileText, Upload, X } from "lucide-react";
import MultiSelect from "../../../components/common/MultiSelect";
import Select from "../../../components/common/Select";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";
import { convertStringToOptions } from "../../../utils/helpers";
import { MAX_FILESIZE_MB, SIZE_KB } from "../../../constants";

const QualificationForm = ({
  qualification = {},
  onSubmit,
  onCancel,
  certificateUrl = "",
  errors,
  setErrors,
  dropDownOptions,
}) => {
  const [formData, setFormData] = useState({
    id: qualification?.id || null,
    // education_level_display: qualification?.education_level_display || "",
    highest_education_level: qualification?.highest_education_level || "",
    other_highest_education_level: qualification?.other_highest_education_level || "",
    course_of_study: qualification?.course_of_study || "",
    institution: qualification?.institution || "",
    expected_graduation_year: qualification?.expected_graduation_year || "",
    aviation_certifications: qualification?.aviation_certifications || "",
    certificate_upload: qualification?.certificate_upload || null,
    gpa: qualification?.gpa || "",
    aviation_specification: qualification?.aviation_specification || "",
    other_aviation_specification: qualification?.other_aviation_specification || "",
    professional_development_courses:
      qualification?.professional_development_courses || "",
    workshops_attended: qualification?.workshops_attended || "",
    other_workshops_attended: qualification?.other_workshops_attended || "",
  });

  const [fileError, setFileError] = useState("");

  const currentYear = new Date().getFullYear();
  const graduationYears = Array.from({ length: 10 }, (_, i) => currentYear + i);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const updatedErrors = { ...errors };
      delete updatedErrors[field];
      setErrors(updatedErrors);
    }
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {};

    if (!formData.highest_education_level?.trim()) {
      newErrors.highest_education_level = "Education level is required";
      valid = false;
    } else if (formData.highest_education_level === "other" && !formData.other_highest_education_level?.trim()) {
      newErrors.other_highest_education_level = "Please specify education level";
      valid = false;
    }
    if (formData.aviation_specification === "other" && !formData.other_aviation_specification?.trim()) {
      newErrors.other_aviation_specification = "Please specify aviation specification";
      valid = false;
    }
    if (formData.workshops_attended === "other" && !formData.other_workshops_attended?.trim()) {
      newErrors.other_workshops_attended = "Please specify workshop";
      valid = false;
    }
    if (!formData.course_of_study) {
      newErrors.course_of_study = "Course of study is required";
      valid = false;
    }
    if (!formData.institution?.trim()) {
      newErrors.institution = "Institution is required";
      valid = false;
    }
    if (!formData.expected_graduation_year?.trim()) {
      newErrors.expected_graduation_year = "Graduation year is required";
      valid = false;
    }
    if (!formData.certificate_upload) {
      newErrors.certificate_upload = "Certificate is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, certificate_upload: null }));
  };

  const handleFileUpload = (file) => {
    setFileError("");

    if (!file) return;

    // Check file type
    const validTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      setFileError("Please select a JPG, PNG, or PDF file");
      return false;
    }

    // Check file size (5MB)
    if (file.size > MAX_FILESIZE_MB) {
      setFileError(`File size must be less than ${SIZE_KB}MB`);
      return false;
    }

    setFormData((prev) => ({ ...prev, certificate_upload: file }));

    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && errors["certificate_upload"]) {
      const updatedErrors = { ...errors };
      delete updatedErrors["certificate_upload"];
      setErrors(updatedErrors);
    }
    handleFileUpload(file);
  };

  const handleCloseError = () => {
    setFileError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        ...formData,
        workshops_attended:
          formData.workshops_attended === "other"
            ? (formData.other_workshops_attended || "other")
            : formData.workshops_attended,
      };
      onSubmit(payload);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 sentry-mask">
        {/* Aviation Category */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.highest_education_level}
                helperText={errors.highest_education_level}
                label={
                  <span>
                    Highest Education Level <RequiredAsterisk />
                  </span>
                }
                onChange={(value) =>
                  handleChange("highest_education_level", value)
                }
                options={dropDownOptions.educationLevelChoices}
                value={formData.highest_education_level}
              />
            </Grid>
            {formData.highest_education_level === "other" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="other_highest_education_level"
                  label={
                    <span>
                      Specify Education Level <RequiredAsterisk />
                    </span>
                  }
                  value={formData.other_highest_education_level}
                  onChange={(e) =>
                    handleChange("other_highest_education_level", e.target.value)
                  }
                  error={!!errors.other_highest_education_level}
                  helperText={errors.other_highest_education_level}
                  fullWidth
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={
                  <span>
                    Course of Study <RequiredAsterisk />
                  </span>
                }
                id="course_of_study"
                value={formData.course_of_study}
                onChange={(e) =>
                  handleChange("course_of_study", e.target.value)
                }
                error={!!errors.course_of_study}
                helperText={errors.course_of_study}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={
                  <span>
                    Institution <RequiredAsterisk />
                  </span>
                }
                id="institution"
                value={formData.institution}
                onChange={(e) => handleChange("institution", e.target.value)}
                error={!!errors.institution}
                helperText={errors.institution}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={
                  <span>
                    Graduation Year <RequiredAsterisk />
                  </span>
                }
                id="expected_graduation_year"
                placeholder="2025"
                value={formData.expected_graduation_year}
                onChange={(e) =>
                  handleChange("expected_graduation_year", e.target.value)
                }
                error={!!errors.expected_graduation_year}
                helperText={errors.expected_graduation_year}
                fullWidth
              />
            </Grid>
            {/* <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={
                  <span>
                    GPA <RequiredAsterisk />
                  </span>
                }
                id="gpa"
                placeholder="GPA (e.g., 8.00)"
                value={formData.gpa}
                onChange={(e) => handleChange("gpa", e.target.value)}
                error={!!errors.gpa}
                helperText={errors.gpa}
                fullWidth
              />
            </Grid> */}

            {/* Aviation Specification */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.aviation_specification}
                helperText={errors.aviation_specification}
                label={<span>Aviation Specification</span>}
                onChange={(value) =>
                  handleChange("aviation_specification", value)
                }
                options={dropDownOptions.aviationSpecificationChoices}
                value={formData.aviation_specification}
              />
            </Grid>

            {formData.aviation_specification === "other" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="other_aviation_specification"
                  label={
                    <span>
                      Specify Specification <RequiredAsterisk />
                    </span>
                  }
                  value={formData.other_aviation_specification}
                  onChange={(e) =>
                    handleChange("other_aviation_specification", e.target.value)
                  }
                  error={!!errors.other_aviation_specification}
                  helperText={errors.other_aviation_specification}
                  fullWidth
                />
              </Grid>
            )}

            {/* Workshops Attended */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.workshops_attended}
                helperText={errors.workshops_attended}
                label="Workshops Attended"
                onChange={(value) => handleChange("workshops_attended", value)}
                options={dropDownOptions.workshopChoices}
                value={formData.workshops_attended}
              />
            </Grid>

            {formData.workshops_attended === "other" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="other_workshops_attended"
                  label={
                    <span>
                      Specify Workshop <RequiredAsterisk />
                    </span>
                  }
                  value={formData.other_workshops_attended}
                  onChange={(e) =>
                    handleChange("other_workshops_attended", e.target.value)
                  }
                  error={!!errors.other_workshops_attended}
                  helperText={errors.other_workshops_attended}
                  fullWidth
                />
              </Grid>
            )}

            {/* Professional Development Courses */}
            <Grid size={{ xs: 12 }}>
              <MultiSelect
                id="professional_development_courses"
                label="Professional Development Courses"
                options={dropDownOptions.developmentCourseChoices}
                value={convertStringToOptions(
                  formData.professional_development_courses,
                  dropDownOptions.developmentCourseChoices
                )}
                onChange={(value) =>
                  handleChange("professional_development_courses", value)
                }
                limitTags={3}
                error={!!errors.professional_development_courses}
                helperText={errors.professional_development_courses}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Aviation Certifications"
                id="aviation_certifications"
                placeholder="Aviation Certifications"
                value={formData.aviation_certifications}
                onChange={(e) =>
                  handleChange("aviation_certifications", e.target.value)
                }
                error={!!errors.aviation_certifications}
                helperText={errors.aviation_certifications}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <InputLabel htmlFor="certificate-upload" sx={{ fontWeight: 600 }}>
                Certificate Upload <RequiredAsterisk />
              </InputLabel>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors duration-200"
                onClick={() =>
                  document.getElementById(`certificate-upload`)?.click()
                }
              >
                <input
                  type="file"
                  id={`certificate-upload`}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />

                {formData.certificate_upload ? (
                  <div className="flex items-center justify-between bg-blue-50 rounded-md p-3">
                    <div className="flex items-center">
                      <FileText className="text-blue-500 mr-2" size={20} />
                      <Typography
                        component="a"
                        href={
                          typeof formData.certificate_upload === "string"
                            ? `${certificateUrl}${formData.certificate_upload}`
                            : URL.createObjectURL(formData.certificate_upload) // preview for new upload
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 font-medium hover:underline"
                      >
                        {typeof formData.certificate_upload === "string"
                          ? formData.certificate_upload.split("/").pop()
                          : formData.certificate_upload.name || "Not named"}
                      </Typography>
                    </div>
                    <IconButton
                      onClick={(e) => handleRemoveFile(e)}
                      size="small"
                      className="!text-red-500"
                    >
                      <X size={16} />
                    </IconButton>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-400 mb-2" size={24} />
                    <p className="text-gray-500">Upload Certificate</p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, JPG, PNG up to 5MB
                    </p>
                  </>
                )}
              </div>
              {errors[`certificate_upload`] && (
                <Typography variant="caption" color="error">
                  {errors[`certificate_upload`]}
                </Typography>
              )}
            </Grid>
          </Grid>
        </LocalizationProvider>
        <div className="flex justify-end space-x-2 mt-4">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </div>
      </form>
      <Snackbar
        open={!!fileError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          className="flex items-center"
        >
          {fileError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QualificationForm;
